import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { listenTrialMaxWords, validTextLength } from '@/environment';
import { activePlayerIdAtom, PlayerMode, useAudioPlayer } from '@/hooks/useAudioPlayer';
import Loading from '@/images/Loading.svg';
import PlayPreview from '@/images/PlayPreview.svg';
import VolumePreview from '@/images/VolumePreview.svg';
import { MAIN_PLAYER_ID } from '@/pages/AdvancedEditor/components/panels/script/TextSection';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { AudioError, DEFAULT_VOICE_CONFIG } from '@/state/presenters';
import { getWordsFromText } from '@/utils/utils';
import { IVoice, PlanGroup, Providers, VoiceAccess, VoiceConfig } from '@d-id/types';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

const HeaderButton = styled.button`
    border: none;
    height: 2rem;
    width: 2rem;
    box-sizing: border-box;
    padding: 0;
    color: inherit;

    :disabled {
        opacity: 0.5;
    }
`;

const VoicePlayerButton = styled(HeaderButton)`
    background-color: transparent;
    cursor: pointer;
    width: 40px;
    height: 40px;
    outline: none;

    :disabled {
        cursor: default;
    }
`;

const ListenImage = styled.img`
    height: 2rem;
    width: 2rem;
`;

const ImageWrapper = styled.div<{ isPlaying: boolean }>`
    height: 100%;
    width: 100%;
    background-color: ${({ theme, isPlaying }) => (isPlaying ? theme.colors.black[900] : '#FFF')};
    color: ${({ theme, isPlaying }) => (isPlaying ? '#FFF' : theme.colors.black[900])};
    border-radius: 50%;
    border: 1.5px solid ${({ theme }) => theme.colors.black[200]};
    margin-top: -0.3rem;
    display: flex;
    align-items: center;
    justify-content: center;

    :hover {
        opacity: 0.8;
    }
`;

interface Props {
    disabled?: boolean;
    voice: IVoice;
    text: string;
    voiceConfig?: VoiceConfig;
    ttsError?: AudioError | null;
    setTtsError?: (error: AudioError | null) => void;
}

export const VoicePlayer = observer(({ disabled, voice, text, voiceConfig = DEFAULT_VOICE_CONFIG, ttsError, setTtsError }: Props) => {
    const mixpanel = useAnalytics();
    const { store } = useAdvancedEditorContext();

    const activePlayerId = useRecoilValue(activePlayerIdAtom);
    const playerId = MAIN_PLAYER_ID;

    const isSupportedProvider = voice.provider !== Providers.Amazon;
    const isExternalPrivate = voice.access === VoiceAccess.ExternalPrivate;
    const isOtherVoicePlaying = !!activePlayerId && activePlayerId !== playerId;

    const validLength = text?.length >= validTextLength;
    const { user, isAnonymous } = useAuth();
    const isTrialOrAnonymous = isAnonymous || user?.planGroup === PlanGroup.TRIAL;
    const isScenesPreviewPlaying = store?.isPlaying;
    const listenDisabled = useMemo(
        () => !validLength || disabled || isExternalPrivate || !isSupportedProvider || isOtherVoicePlaying,
        [validLength, disabled, isSupportedProvider, activePlayerId, playerId]
    );
    const allowedText = isTrialOrAnonymous ? getWordsFromText(text, listenTrialMaxWords) : text;

    const { playerMode, error, play, pause } = useAudioPlayer({
        voice,
        text: allowedText,
        voiceConfig,
        language: voice.language,
        playerId,
        ttsError,
        setTtsError,
    });

    function onPlay(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        e.stopPropagation();
        mixpanel?.track('tts-listen', {
            from: 'text-input',
            event: 'play',
            voice,
            style: voiceConfig.style,
            textLength: allowedText.length,
            originalLength: text?.length,
        });

        if (!voice) {
            return;
        }

        if (isScenesPreviewPlaying) {
            store.stop();
        }

        play({ useIndicators: true, volume: 1 });
    }

    function onPause() {
        pause?.();
    }

    const tooltipText = useMemo(() => {
        if (!isSupportedProvider) {
            return "Preview isn't available for this voice";
        }
        return 'Listen';
    }, [isSupportedProvider, listenDisabled]);

    useEffect(() => pause, [pause]);

    useEffect(() => {
        if (error) {
            setTtsError?.(error);
            mixpanel?.track('tts-listen', {
                event: 'error',
                error,
                textLength: allowedText.length,
                originalLength: text?.length,
                voice,
                style: voiceConfig.style,
            });
            if (error === 'unknown') {
                logger.error('Error playing TTS', { error, text, voice, style: voiceConfig.style });
            }
        }
    }, [error]);

    useEffect(() => {
        if (isScenesPreviewPlaying && playerMode === PlayerMode.Playing) {
            onPause();
        }
    }, [isScenesPreviewPlaying]);

    if (playerMode === PlayerMode.Loading && playerId === activePlayerId && !isScenesPreviewPlaying) {
        return (
            <CustomTooltip title="" placement="top" cursor="pointer" id="play-btn">
                <VoicePlayerButton disabled type="button">
                    <ImageWrapper isPlaying={false}>
                        <ListenImage src={Loading} />
                    </ImageWrapper>
                </VoicePlayerButton>
            </CustomTooltip>
        );
    }

    if (playerMode === PlayerMode.Playing && playerId === activePlayerId && !isScenesPreviewPlaying) {
        return (
            <CustomTooltip title="" placement="top" disabled={!voice} cursor="pointer" onClick={onPause}>
                <VoicePlayerButton disabled={!voice} id="play-btn" type="button">
                    <ImageWrapper isPlaying>
                        <MaskedIcon src={VolumePreview} size="20px" />
                    </ImageWrapper>
                </VoicePlayerButton>
            </CustomTooltip>
        );
    }

    return (
        <CustomTooltip title={tooltipText} placement="top" onClick={onPlay} disabled={listenDisabled} cursor={'pointer'}>
            <VoicePlayerButton aria-label="play preview" disabled={listenDisabled} id="play-btn" type="button">
                <ImageWrapper isPlaying={false}>
                    <MaskedIcon src={PlayPreview} size="20px" />
                </ImageWrapper>
            </VoicePlayerButton>
        </CustomTooltip>
    );
});
